import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isLocationGoneError,
  isRetryableStateError,
  isStaleStateError,
  resolveAccessError,
} from "./access-errors";

interface UseAccessSubmitOptions<Result> {
  /** The write (`mutateAsync(...)`). Its failure is classified here. */
  readonly run: () => Promise<Result>;
  /** The mutation's `isPending`: closing is blocked while it is true. */
  readonly isPending: boolean;
  /** The form is valid; a submit while false is ignored. Defaults to true. */
  readonly canSubmit?: boolean;
  /** The API accepted the write. The caller decides whether the dialog closes. */
  readonly onSuccess: (result: Result) => void;
  /**
   * The thing the dialog was acting on is not what it assumed (gone, changed
   * role...). The reads were refreshed; the dialog closes and hands the reason over.
   * Omit it for a dialog that keeps the form open and just shows the error.
   */
  readonly onStale?: (message: string) => void;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * First look at a failure: return true when the caller dealt with it (for
   * example a dialog-specific conflict), which skips the default policy.
   */
  readonly onFailure?: (err: unknown) => boolean;
  /** A picked location is gone: runs after the refresh, to drop it from the selection. */
  readonly onLocationGone?: () => Promise<void> | void;
}

export interface AccessSubmit {
  readonly submit: () => Promise<void>;
  /** Our own copy for the last failure (never the server's message), or null. */
  readonly error: string | null;
  /** Forgets the last failure (the form changed under it). */
  readonly clearError: () => void;
  /** Closing is refused while the write is in flight. */
  readonly handleOpenChange: (next: boolean) => void;
}

/**
 * The submit scaffold every access dialog shares: a synchronous double-submit
 * guard (`isPending` only flips after a re-render), and the error policy.
 * - Out-of-date state (`isStaleStateError`): `onStale(message)` and close (when
 *   `onStale` is given; otherwise it is an ordinary error).
 * - A retryable state change (`MEMBERSHIP_STATE_CHANGED`): the reads were
 *   refreshed, the dialog stays open with the error and the selection intact.
 * - A picked location is gone: the error stays, `onLocationGone` prunes the selection.
 * - Anything else: the error stays and the operator can retry.
 */
export function useAccessSubmit<Result>({
  run,
  isPending,
  canSubmit = true,
  onSuccess,
  onStale,
  onOpenChange,
  onLocationGone,
  onFailure,
}: UseAccessSubmitOptions<Result>): AccessSubmit {
  const { t } = useTranslation();
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const handleFailure = async (err: unknown) => {
    if (onFailure?.(err)) return;
    const message = resolveAccessError(err, t);
    if (onStale && isStaleStateError(err) && !isRetryableStateError(err)) {
      onStale(message);
      onOpenChange(false);
      return;
    }
    setError(message);
    if (isLocationGoneError(err)) await onLocationGone?.();
  };

  const submit = async () => {
    if (!canSubmit || isPending || inFlight.current) return;
    inFlight.current = true;
    setError(null);
    try {
      let result: Result;
      try {
        result = await run();
      } catch (err) {
        await handleFailure(err);
        return;
      }
      // Outside the try above: the write succeeded, so a throwing host callback
      // must never be shown to the operator as an access error.
      try {
        onSuccess(result);
      } catch (err) {
        console.error("Access dialog: onSuccess callback failed", err);
      }
    } finally {
      inFlight.current = false;
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return;
    onOpenChange(next);
  };

  return { submit, error, clearError: () => setError(null), handleOpenChange };
}
